import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import googleIcon from "@images/icons/social/google.svg";
import avatarNarges from "@images/reviews/narges-nourelden.webp";
import avatarSaad from "@images/reviews/mohamed-saad.webp";
import avatarEsraa from "@images/reviews/esraa-ayman.webp";
import avatarYossef from "@images/reviews/yossef-hosnii.webp";
import avatarSaleh from "@images/reviews/mohamed-saleh.webp";
import {
    GOOGLE_MAPS_URL,
    GOOGLE_PLACE_ID,
    GOOGLE_RATING,
    GOOGLE_REVIEW_COUNT,
} from "@/Components/GoogleRatingBadge";

// Our own Google reviews block (replaces the Trustindex carousel, whose
// trial expired). REAL reviews only, copied from our Google Maps listing on
// 2026-10-19: original text (not Google's translation), the reviewer's own
// Google avatar, and the review date. Google shows logged-out visitors only
// these 5 of the 15; never add a review that isn't on the listing.
// Mohamed Saad's exact day isn't in Google's page data ("قبل سنتين"), so his
// date is the same month as the others.
const REVIEWS = [
    { name: "narges nourelden", avatar: avatarNarges, date: "2024-07-20", text: "من افضل الشركات وتتمتع بالمصداقية وسرعة انجاز الاعمال وجودتها العالية" },
    { name: "Mohamed Saad", avatar: avatarSaad, date: "2024-07-20", text: "إحترافية في التعامل والتنفيذ وجودة محترمة ♥️🙏🏻" },
    { name: "Esraa Ayman", avatar: avatarEsraa, date: "2024-07-20", text: "❤️ The best in everything" },
    { name: "Yossef Hosnii", avatar: avatarYossef, date: "2024-07-21", text: "شركة محترفة فى مجالها بجد وسرعة فى التسليم" },
    { name: "Mohamed Saleh", avatar: avatarSaleh, date: "2024-07-20", text: "Great company for all social media services 💯❣️" },
];

const WRITE_REVIEW_URL = `https://search.google.com/local/writereview?placeid=${GOOGLE_PLACE_ID}`;

const timeAgo = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    let months = (now.getFullYear() - d.getFullYear()) * 12 + now.getMonth() - d.getMonth();
    if (now.getDate() < d.getDate()) months -= 1;
    if (months < 1) return "منذ أيام";
    if (months < 12) {
        if (months === 1) return "منذ شهر";
        if (months === 2) return "منذ شهرين";
        return months <= 10 ? `منذ ${months} أشهر` : `منذ ${months} شهرًا`;
    }
    const y = Math.floor(months / 12);
    if (y === 1) return "منذ سنة";
    if (y === 2) return "منذ سنتين";
    return y <= 10 ? `منذ ${y} سنوات` : `منذ ${y} سنة`;
};

// Scoped CSS, not Tailwind: the pinned live app CSS lacks new utility classes.
const CSS = `
.wz-grv{--wz-grv-bg:#f4f4f5;color:#111827}
.wz-grv-head{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:18px 28px;margin-bottom:16px;background:var(--wz-grv-bg);border-radius:20px}
.wz-grv-sum{display:flex;align-items:center;flex-wrap:wrap;gap:8px 12px;text-decoration:none;color:inherit}
.wz-grv-sum-label{font-size:20px;font-weight:800;line-height:1}
.wz-grv-sum-score{font-size:20px;font-weight:800;line-height:1}
.wz-grv-sum-sep{width:1px;height:20px;background:#9ca3af}
.wz-grv-sum-count{font-size:17px;font-weight:700;line-height:1}
.wz-grv-word{font-family:Arial,Helvetica,sans-serif;font-size:30px;font-weight:500;letter-spacing:-.5px;line-height:1;direction:ltr}
.wz-grv-word span:nth-child(1),.wz-grv-word span:nth-child(4){color:#4285F4}
.wz-grv-word span:nth-child(2),.wz-grv-word span:nth-child(6){color:#EA4335}
.wz-grv-word span:nth-child(3){color:#FBBC05}
.wz-grv-word span:nth-child(5){color:#34A853}
.wz-grv-write{display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 24px;border:1.5px solid #6b7280;border-radius:10px;background:#fff;font-size:16px;font-weight:700;color:#111827;text-decoration:none;white-space:nowrap;transition:border-color .2s,color .2s}
.wz-grv-write:hover{border-color:#2563eb;color:#2563eb}
.wz-grv-stars{display:inline-flex;align-items:center;gap:2px;direction:ltr}
.wz-grv-stars svg{display:block;fill:#F5B301}
.wz-grv-body{display:flex;align-items:center}
.wz-grv-track{flex:1;min-width:0;display:grid;grid-auto-flow:column;grid-auto-columns:calc((100% - 32px)/3);gap:16px;overflow-x:auto;scroll-snap-type:x mandatory;scroll-behavior:smooth;scrollbar-width:none;-webkit-overflow-scrolling:touch}
.wz-grv-track::-webkit-scrollbar{display:none}
.wz-grv-card{scroll-snap-align:start;display:flex;flex-direction:column;gap:12px;padding:24px;background:var(--wz-grv-bg);border-radius:20px;min-height:200px}
.wz-grv-card-head{display:flex;align-items:center;gap:12px}
.wz-grv-card-head>img:first-child{width:48px;height:48px;border-radius:999px;flex:0 0 48px;object-fit:cover}
.wz-grv-who{flex:1;min-width:0;display:flex;flex-direction:column;gap:4px}
.wz-grv-name{font-size:17px;font-weight:800;line-height:1.2;text-align:right;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.wz-grv-ago{font-size:13px;color:#6b7280;line-height:1;text-align:right}
.wz-grv-g{width:22px;height:22px;flex:0 0 22px;align-self:flex-start}
.wz-grv-rate{display:flex;align-items:center;gap:8px}
.wz-grv-rate>svg{width:18px;height:18px;flex:0 0 18px}
.wz-grv-text{margin:0;font-size:16px;line-height:1.75;text-align:right;color:#1f2937}
.wz-grv-arrow{position:relative;z-index:1;flex:0 0 44px;width:44px;height:44px;display:flex;align-items:center;justify-content:center;margin-inline:-22px;border-radius:999px;border:1px solid #e5e7eb;background:#fff;color:#374151;box-shadow:0 2px 10px rgba(15,23,42,.12);cursor:pointer;transition:opacity .2s,color .2s}
.wz-grv-arrow:hover{color:#2563eb}
.wz-grv-arrow:disabled{opacity:0;pointer-events:none}
.wz-grv-arrow svg{width:22px;height:22px}
@media (max-width:1023px){.wz-grv-track{grid-auto-columns:calc((100% - 16px)/2)}}
@media (max-width:767px){
.wz-grv-head{flex-direction:column-reverse;gap:12px;padding:16px;border-radius:16px;margin-bottom:12px}
.wz-grv-sum{justify-content:center;gap:6px 10px}
.wz-grv-sum-label,.wz-grv-sum-score{font-size:17px}
.wz-grv-sum-sep{flex-basis:100%;width:0;height:0}
.wz-grv-sum-count{font-size:15px}
.wz-grv-word{font-size:22px}
.wz-grv-write{width:100%;font-size:15px}
.wz-grv-arrow{display:none}
.wz-grv-track{grid-auto-columns:86%;gap:12px}
.wz-grv-card{padding:16px;gap:10px;border-radius:16px;min-height:0}
.wz-grv-card-head>img:first-child{width:44px;height:44px;flex-basis:44px}
.wz-grv-name{font-size:15px}
.wz-grv-text{font-size:15px;line-height:1.7}
}`;

const Stars = ({ size }) => (
    <span className="wz-grv-stars" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
            <svg key={i} width={size} height={size} viewBox="0 0 24 24">
                <path d="M12 2.5l2.94 6.06 6.56.92-4.78 4.6 1.17 6.52L12 17.5l-5.89 3.1 1.17-6.52-4.78-4.6 6.56-.92z" />
            </svg>
        ))}
    </span>
);

const VerifiedMark = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M12 1.5l2.6 1.9 3.2-.1 1 3 2.6 1.9-1 3.1 1 3.1-2.6 1.9-1 3-3.2-.1L12 22.5l-2.6-1.9-3.2.1-1-3-2.6-1.9 1-3.1-1-3.1 2.6-1.9 1-3 3.2.1z" />
        <path fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" d="M7.8 12.2l2.8 2.8 5.6-5.8" />
    </svg>
);

const GoogleReviewsCarousel = () => {
    const trackRef = useRef(null);
    const [edges, setEdges] = useState({ start: true, end: false });

    // RTL: scrollLeft runs 0 → negative, so compare magnitudes.
    const update = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        const pos = Math.abs(el.scrollLeft);
        setEdges({ start: pos < 4, end: pos + el.clientWidth >= el.scrollWidth - 4 });
    }, []);

    useEffect(() => {
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [update]);

    // dir=rtl: "next" reveals cards to the left (negative scroll).
    const go = (dir) => {
        const el = trackRef.current;
        const card = el?.firstElementChild;
        if (!card) return;
        const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(el).columnGap || 0);
        el.scrollBy({ left: dir * -step, behavior: "smooth" });
    };

    return (
        <div className="wz-grv" dir="rtl">
            <style>{CSS}</style>

            {/* RTL: first child sits on the right. */}
            <div className="wz-grv-head">
                <a className="wz-grv-write" href={WRITE_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                    كتابة تقييم
                </a>
                <a
                    className="wz-grv-sum"
                    href={GOOGLE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`تقييم ${GOOGLE_RATING} من 5 من ${GOOGLE_REVIEW_COUNT} مراجعة على Google`}
                >
                    <span className="wz-grv-sum-label">ممتاز</span>
                    <Stars size={22} />
                    <span className="wz-grv-sum-score">{GOOGLE_RATING}</span>
                    <span className="wz-grv-sum-sep" aria-hidden="true" />
                    <span className="wz-grv-sum-count">{GOOGLE_REVIEW_COUNT} من المراجعات</span>
                    <span className="wz-grv-word" aria-hidden="true">
                        <span>G</span><span>o</span><span>o</span><span>g</span><span>l</span><span>e</span>
                    </span>
                </a>
            </div>

            <div className="wz-grv-body">
                <button type="button" className="wz-grv-arrow" onClick={() => go(-1)} disabled={edges.start} aria-label="المراجعة السابقة">
                    <ChevronRight />
                </button>
                <div className="wz-grv-track" ref={trackRef} onScroll={update}>
                    {REVIEWS.map((r) => (
                        <article key={r.name} className="wz-grv-card">
                            <div className="wz-grv-card-head">
                                <img src={r.avatar} alt={r.name} width="48" height="48" loading="lazy" decoding="async" />
                                <div className="wz-grv-who">
                                    <span className="wz-grv-name" dir="auto">{r.name}</span>
                                    <span className="wz-grv-ago">{timeAgo(r.date)}</span>
                                </div>
                                <img className="wz-grv-g" src={googleIcon} alt="Google" width="22" height="22" loading="lazy" />
                            </div>
                            <div className="wz-grv-rate" aria-label="5 من 5 نجوم">
                                <Stars size={20} />
                                <VerifiedMark />
                            </div>
                            <p className="wz-grv-text" dir="auto">{r.text}</p>
                        </article>
                    ))}
                </div>
                <button type="button" className="wz-grv-arrow" onClick={() => go(1)} disabled={edges.end} aria-label="المراجعة التالية">
                    <ChevronLeft />
                </button>
            </div>
        </div>
    );
};

export default GoogleReviewsCarousel;
